// =============================================
// PÁGINA DE RELATÓRIO DE CHECKLISTS
// =============================================

import React, { useState } from 'react';
import { apiService } from '../../services/api';
import FilterPanel from '../../components/Common/FilterPanel';
import ReportActions from '../../components/Reports/ReportActions';
import ResultsCounter from '../../components/Common/ResultsCounter';
import LoadMoreButton from '../../components/Common/LoadMoreButton';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { getToken, Config } from '../../services/config';
import { printReport } from '../../utils/printReport';
import './Reports.css';

const ChecklistsReport: React.FC = () => {
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [estatisticas, setEstatisticas] = useState<any>(null);
  const [filters, setFilters] = useState<Record<string, any>>({
    dataInicio: '',
    dataFim: '',
    status: '',
    veiculoId: '',
    motoristaId: '',
    equipamentoId: ''
  });

  const {
    items: checklistsData,
    isLoading,
    totalItems,
    displayedItems,
    hasMore,
    loadMore,
    reset,
    loadAll
  } = useInfiniteScroll<any>({
    fetchFunction: async (page, limit, appliedFilters) => {
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('page', page.toString());
        queryParams.append('limit', limit.toString());

        Object.entries(appliedFilters || {}).forEach(([key, value]) => {
          if (value) {
            queryParams.append(key, String(value));
          }
        });

        const response = await apiService.getCustom<any>(
          `/relatorios/checklists?${queryParams.toString()}`
        );

        if (response.success && response.data) {
          // Armazenar estatísticas na primeira carga
          if (page === 1 && response.data.estatisticas) {
            setEstatisticas(response.data.estatisticas);
          }

          return {
            success: true,
            data: {
              items: response.data.relatorio || [],
              pagination: {
                total: response.data.pagination?.total || response.data.relatorio?.length || 0
              }
            }
          };
        }

        throw new Error('Erro ao carregar relatório');
      } catch (err: any) {
        console.error('Erro ao buscar relatório:', err);
        setError(err.message || 'Erro ao carregar relatório');
        return {
          success: false,
          data: [],
          message: err.message || 'Erro ao carregar relatório'
        };
      }
    },
    itemsPerPage: 10,
    filters: filters
  });

  const handleExportPDF = async () => {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) queryParams.append(key, String(value));
    });

    const token = getToken();

    if (!token) {
      alert('Sessão expirada. Faça login novamente.');
      return;
    }

    const url = `${Config.API_BASE_URL}/relatorios/exportar/checklists/pdf?${queryParams.toString()}`;

    try {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `relatorio-checklists-${Date.now()}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.message || 'Erro ao exportar PDF. Tente novamente.');
      }
    } catch (err) {
      console.error('Erro ao exportar PDF:', err);
      alert('Erro ao exportar PDF. Verifique sua conexão.');
    }
  };

  const handleExportCSV = async () => {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) queryParams.append(key, String(value));
    });

    const token = getToken();

    if (!token) {
      alert('Sessão expirada. Faça login novamente.');
      return;
    }

    const url = `${Config.API_BASE_URL}/relatorios/exportar/checklists/csv?${queryParams.toString()}`;

    try {
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `relatorio-checklists-${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.message || 'Erro ao exportar CSV. Tente novamente.');
      }
    } catch (err) {
      console.error('Erro ao exportar CSV:', err);
      alert('Erro ao exportar CSV. Verifique sua conexão.');
    }
  };

  const handlePrint = async () => {
    // Carrega TODOS os dados antes de imprimir
    await loadAll();
    // Aguarda um pouco para garantir que o DOM foi atualizado
    setTimeout(() => {
      printReport();
    }, 100);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-left">
          <h1>
            <i className="fas fa-clipboard-list"></i>
            Relatório de Checklists
          </h1>
          <p className="report-subtitle">Análise detalhada dos checklists realizados</p>
        </div>
      </div>

      <div className="page-content">
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Filtros e Ações</h2>
            <FilterPanel
              fields={[
                {
                  type: 'date',
                  label: 'Data Início',
                  value: filters.dataInicio,
                  onChange: (value) => setFilters(prev => ({ ...prev, dataInicio: value }))
                },
                {
                  type: 'date',
                  label: 'Data Fim',
                  value: filters.dataFim,
                  onChange: (value) => setFilters(prev => ({ ...prev, dataFim: value }))
                },
                {
                  type: 'select',
                  label: 'Status',
                  value: filters.status,
                  onChange: (value) => setFilters(prev => ({ ...prev, status: value })),
                  options: [
                    { value: 'pendente', label: 'Pendente' },
                    { value: 'em_andamento', label: 'Em Andamento' },
                    { value: 'concluido', label: 'Concluído' },
                    { value: 'reprovado', label: 'Reprovado' }
                  ]
                }
              ]}
              onFilter={reset}
              onClear={() => {
                setFilters({
                  dataInicio: '',
                  dataFim: '',
                  status: '',
                  veiculoId: '',
                  motoristaId: '',
                  equipamentoId: ''
                });
              }}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              title="Filtros de Relatório"
            />
          </div>

          <div className="card-body">
            {error && (
              <div className="alert alert-danger">
                <i className="fas fa-exclamation-circle"></i>
                {error}
              </div>
            )}

            {!isLoading && checklistsData && checklistsData.length > 0 && (
              <>
                <ReportActions
                  onExportPDF={handleExportPDF}
                  onExportCSV={handleExportCSV}
                  onPrint={handlePrint}
                  loading={isLoading}
                />

                {estatisticas && (
                  <div className="report-statistics">
                    <div className="stat-card">
                      <div className="stat-icon stat-icon-primary">
                        <i className="fas fa-clipboard-check"></i>
                      </div>
                      <div className="stat-content">
                        <h4>Total de Checklists</h4>
                        <p className="stat-value">{estatisticas.total_checklists}</p>
                      </div>
                    </div>

                    <div className="stat-card">
                      <div className="stat-icon stat-icon-success">
                        <i className="fas fa-check-circle"></i>
                      </div>
                      <div className="stat-content">
                        <h4>Itens Preenchidos</h4>
                        <p className="stat-value">{estatisticas.total_preenchidos}</p>
                      </div>
                    </div>

                    <div className="stat-card">
                      <div className="stat-icon stat-icon-warning">
                        <i className="fas fa-list"></i>
                      </div>
                      <div className="stat-content">
                        <h4>Total de Itens</h4>
                        <p className="stat-value">{estatisticas.total_itens}</p>
                      </div>
                    </div>

                    <div className="stat-card">
                      <div className="stat-icon stat-icon-info">
                        <i className="fas fa-percentage"></i>
                      </div>
                      <div className="stat-content">
                        <h4>Taxa de Preenchimento</h4>
                        <p className="stat-value">{estatisticas.taxa_preenchimento_geral}%</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="report-content">
                  <div className="report-table-header">
                    <h3>Dados Detalhados</h3>
                  </div>
                  <div className="report-table-container">
                    {/* Contador de resultados */}
                    <ResultsCounter
                      displayedItems={displayedItems}
                      totalItems={totalItems}
                      isLoading={isLoading}
                      label="checklist(s)"
                    />

                    <table className="report-table">
                      <thead>
                        <tr>
                          <th>Data</th>
                          <th>Veículo</th>
                          <th>Tipo Veículo</th>
                          <th>Usuário</th>
                          <th>Status</th>
                          <th>Itens</th>
                          <th>Preenchimento</th>
                          <th>Ocorrências</th>
                        </tr>
                      </thead>
                      <tbody>
                        {checklistsData.map((item: any) => (
                          <tr key={item.id}>
                            <td>{new Date(item.data_criacao).toLocaleDateString('pt-BR')}</td>
                            <td>
                              <strong>{item.veiculo_placa || '-'}</strong><br />
                              <small>{item.veiculo_modelo || ''}</small>
                            </td>
                            <td>{item.veiculo_tipo || '-'}</td>
                            <td>{item.motorista_nome || '-'}</td>
                            <td>
                              <span className={`badge badge-${item.status === 'concluido' || item.status === 'completado' ? 'success' : item.status === 'reprovado' ? 'danger' : 'warning'}`}>
                                {item.status}
                              </span>
                            </td>
                            <td>{item.total_itens || 0}</td>
                            <td>
                              <div className="progress-bar-container">
                                <div className="progress-bar-fill" style={{ width: `${item.taxa_preenchimento}%` }}></div>
                                <span className="progress-bar-text">{item.taxa_preenchimento}%</span>
                              </div>
                            </td>
                            <td>{item.total_ocorrencias || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Botão "Ver Mais" */}
                    <LoadMoreButton
                      onClick={loadMore}
                      isLoading={isLoading}
                      hasMore={hasMore}
                    />
                  </div>
                </div>
              </>
            )}

            {isLoading && checklistsData.length === 0 && (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Carregando relatório...</p>
              </div>
            )}

            {!isLoading && checklistsData.length === 0 && (
              <div className="no-data">
                <i className="fas fa-inbox"></i>
                <p>Nenhum dado encontrado para os filtros selecionados</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChecklistsReport;
